/**
 * Formatting Utilities for Upload Progress
 * 
 * Helper functions to display file sizes and time estimates
 * in a human-readable format.
 */

/**
 * Formats bytes into human-readable size string
 * 
 * @param bytes - Number of bytes
 * @returns Formatted string like "1.5 MB"
 * 
 * @example
 * formatBytes(1536) // "1.5 KB"
 * formatBytes(1048576) // "1 MB"
 * formatBytes(0) // "0 B"
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const index = Math.min(i, sizes.length - 1);
  
  return `${parseFloat((bytes / Math.pow(k, index)).toFixed(1))} ${sizes[index]}`;
}

/**
 * Formats seconds into human-readable time string
 * 
 * @param seconds - Number of seconds
 * @returns Formatted string like "2 min" or "45s"
 * 
 * @example
 * formatTime(30) // "30s"
 * formatTime(90) // "2 min"
 * formatTime(3700) // "1h"
 */
export function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Formats upload speed into human-readable string
 * 
 * @param bytesPerSecond - Upload speed in bytes per second
 * @returns Formatted string like "2.4 MB/s"
 * 
 * @example
 * formatSpeed(2500000) // "2.4 MB/s"
 */
export function formatSpeed(bytesPerSecond: number): string {
  return `${formatBytes(bytesPerSecond)}/s`;
}
